// The browser-side port of project-service's GradientSvgUtil (Java), which MUST stay
// byte-for-byte equivalent so an AI-placed gradient renders identically to a manually-placed one
// (see docs/plans/design3d-gradient-fills-plan.md's "Backend symmetry" section) - true for every
// call site that leaves the optional center/radius params at their defaults (0.5/0.5/0.5, this
// function's original always-centered behavior). Those params are a client-only addition for the
// interactive radial drag handles (design-3d.component.ts's attachRadialGradientControls()); the
// backend has no way to place an off-center radial yet, so this genuinely can't diverge from it
// until GradientSvgUtil grows the same params - flag that as follow-up work, not a bug here.
//
// Deliberately not a live Fabric.js Gradient assigned to an object's fill - the print engine's own
// DesignObject.fill (builder-cb) is a plain string, so a structured fill value there fails to
// deserialize. Baked to a flat image instead, same as geometric-pattern.util.ts: generated at
// exactly the target pixel size, so the caller places it the same simple way it already places any
// other image.

export type GradientType = 'LINEAR' | 'RADIAL';

/**
 * @param type "LINEAR" or "RADIAL" (case-insensitive; anything else falls back to LINEAR)
 * @param fromColor the gradient's start color
 * @param toColor the gradient's end color
 * @param angleDegrees direction for a LINEAR gradient, matching Fabric's own clockwise-positive
 *   angle convention (0 = left-to-right); ignored for RADIAL
 * @param width the exact SVG width to generate, in pixels
 * @param height the exact SVG height to generate, in pixels
 * @param centerXPercent RADIAL only, 0-1 fraction of width; defaults to 0.5 (centered), matching
 *   this function's original always-centered behavior when omitted - see the interactive drag
 *   handles in design-3d.component.ts's attachRadialGradientControls()
 * @param centerYPercent RADIAL only, 0-1 fraction of height; defaults to 0.5
 * @param radiusPercent RADIAL only, 0-1+ fraction of width; defaults to 0.5
 */
export function svgGradient(
  type: string,
  fromColor: string,
  toColor: string,
  angleDegrees: number,
  width: number,
  height: number,
  centerXPercent = 0.5,
  centerYPercent = 0.5,
  radiusPercent = 0.5,
): string {
  const radial = type?.toUpperCase() === 'RADIAL';
  const fillDefinition = radial
    ? `<radialGradient id="g" cx="${centerXPercent * 100}%" cy="${centerYPercent * 100}%" r="${radiusPercent * 100}%">` +
      `<stop offset="0%" stop-color="${fromColor}"/>` +
      `<stop offset="100%" stop-color="${toColor}"/>` +
      `</radialGradient>`
    : `<linearGradient id="g" x1="0%" y1="0%" x2="100%" y2="0%" gradientTransform="rotate(${angleDegrees} 0.5 0.5)">` +
      `<stop offset="0%" stop-color="${fromColor}"/>` +
      `<stop offset="100%" stop-color="${toColor}"/>` +
      `</linearGradient>`;

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">` +
    `<defs>${fillDefinition}</defs>` +
    `<rect width="100%" height="100%" fill="url(#g)"/>` +
    `</svg>`
  );
}

/**
 * Fast, synchronous approximation of svgGradient(), for the on-canvas gradient drag handles
 * (attachRadialGradientControls()/attachLinearGradientControl() in design-3d(.-redesign).component.ts)
 * to repaint into on every drag tick. Deliberately NOT used for the committed fill - that still goes
 * through svgGradient() via buildGradientFabricImage() on mouseUpHandler, so the print-engine-matching
 * output (see this file's top comment) is unaffected. This exists only so the gradient's rendered
 * colors track the pointer live instead of jumping once on release: baking through svgGradient() and
 * decoding the result as an <img> on every mousemove is an async, image-decode-bound round trip - far
 * too slow to repeat every tick. A canvas gradient is neither, so this draws straight into a caller-
 * owned, caller-reused HTMLCanvasElement instead of returning a string.
 *
 * Canvas 2D's own gradients are circular/axis-aligned only, but svgGradient()'s SVG defaults to
 * objectBoundingBox units, where a RADIAL's cx/cy/r are fractions of a UNIT SQUARE that's then
 * stretched (non-uniformly, when width != height) onto the real box - i.e. a "circle" in that
 * fraction space can render as an ellipse in pixels, and a LINEAR's gradientTransform rotation
 * happens in that same stretched space before the stretch is applied. Reproduced here the same way:
 * everything is drawn in the [0,1]x[0,1] unit square, then that square is scaled up to width x height
 * as the outermost transform, so a mid-drag radial/linear preview lines up with the real bake instead
 * of visibly popping into a different shape/angle the instant mouseUpHandler's commit() replaces it.
 */
export function renderGradientPreview(
  canvas: HTMLCanvasElement,
  type: GradientType,
  fromColor: string,
  toColor: string,
  angleDegrees: number,
  width: number,
  height: number,
  centerXPercent = 0.5,
  centerYPercent = 0.5,
  radiusPercent = 0.5,
): void {
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return;
  }
  const radial = type?.toUpperCase() === 'RADIAL';
  ctx.save();
  ctx.scale(width, height);
  let gradient: CanvasGradient;
  if (radial) {
    gradient = ctx.createRadialGradient(centerXPercent, centerYPercent, 0, centerXPercent, centerYPercent, radiusPercent);
  } else {
    // A CanvasGradient's coordinates are transformed by whatever CTM is active when it's later USED
    // (i.e. at fillRect() time below), not the one active when createLinearGradient() is called - so
    // wrapping just the creation call in its own rotated ctx.rotate()/restore() (as a first attempt
    // here did) has no effect at all, and rotating the shared transform around the fillRect() call
    // instead would rotate the SHAPE being painted too, leaving corners outside it unpainted.
    // Rotating the two endpoints by hand keeps both the gradient and the fillRect below in the same
    // scale-only transform, so only the gradient's ray direction rotates - matching how
    // svgGradient()'s gradientTransform="rotate(angle 0.5 0.5)" only ever rotates the gradient, never
    // the rect it fills.
    const rad = (angleDegrees * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    const rotateAroundCenter = (x: number, y: number): [number, number] => [
      0.5 + (x - 0.5) * cos - (y - 0.5) * sin,
      0.5 + (x - 0.5) * sin + (y - 0.5) * cos,
    ];
    const [x0, y0] = rotateAroundCenter(0, 0);
    const [x1, y1] = rotateAroundCenter(1, 0);
    gradient = ctx.createLinearGradient(x0, y0, x1, y1);
  }
  gradient.addColorStop(0, fromColor);
  gradient.addColorStop(1, toColor);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 1, 1);
  ctx.restore();
}
